import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import RefreshIcon from '@mui/icons-material/Refresh';
import SlowMotionVideoIcon from '@mui/icons-material/SlowMotionVideo';
import ThunderstormIcon from '@mui/icons-material/Thunderstorm';
import {
  Alert,
  Box,
  Chip,
  CircularProgress,
  Divider,
  IconButton,
  Stack,
  Tooltip,
  Typography,
} from '@mui/material';
import type { ImpactBundleTrail, SecondOrderSnapshot } from '@akem/shared';
import React from 'react';

import {
  formatConfidence,
  formatMagnitude,
  getSecondOrderSnapshot,
  resolveInstrumentId,
  splitTrails,
  summarizeExplanation,
} from '../../../services/secondOrder.js';
import type { WidgetProps } from '../types/index.js';
import { ExposureSnapshot } from '../components/ExposureSnapshot.js';

const summarizeAsOf = (value?: string | null): string => {
  if (!value) return '—';
  const trimmed = value.includes('|') ? value.split('|')[0] : value;
  const parsed = new Date(trimmed);
  if (Number.isNaN(parsed.getTime())) return trimmed;
  return parsed.toLocaleString();
};

const pickTierIcon = (tier: ImpactBundleTrail['tier']) =>
  tier === 'fast' ? <ThunderstormIcon fontSize="small" color="primary" /> : <SlowMotionVideoIcon fontSize="small" color="secondary" />;

const ImpactRow: React.FC<{ trail: ImpactBundleTrail }> = ({ trail }) => {
  const explanation = summarizeExplanation(trail, 2) || 'Explanation pending';
  const magnitude = formatMagnitude(trail.bundle.magnitude, trail.bundle.units);
  const confidence = formatConfidence(trail.bundle.confidence);
  const isAnchored = Boolean(trail.bundle.anchored_by_bedrock);
  return (
    <Stack spacing={0.75} sx={{ py: 1 }}>
      <Stack direction="row" spacing={1} alignItems="center">
        <Typography variant="subtitle2" sx={{ flexGrow: 1 }}>
          {trail.bundle.affected_metric}
        </Typography>
        <Tooltip title={isAnchored ? 'Anchored to exposure evidence' : 'Signal-only impact (needs bedrock)'}>
          <Chip
            size="small"
            color={isAnchored ? 'success' : 'warning'}
            icon={isAnchored ? <CheckCircleOutlineIcon fontSize="small" /> : <ErrorOutlineIcon fontSize="small" />}
            label={isAnchored ? 'Anchored' : 'Signal'}
          />
        </Tooltip>
        <Chip
          size="small"
          variant="outlined"
          icon={pickTierIcon(trail.tier)}
          label={trail.tier === 'fast' ? 'Fast stream' : 'Slow stream'}
        />
      </Stack>
      <Stack direction="row" spacing={1.5} alignItems="center">
        <Typography variant="h6" color={trail.bundle.magnitude >= 0 ? 'success.main' : 'error.main'}>
          {magnitude}
        </Typography>
        <Typography variant="body2" color="text.secondary">
          Lag {trail.bundle.lag}
        </Typography>
        <Typography variant="body2" color="text.secondary">
          Confidence {confidence}
        </Typography>
      </Stack>
      <Typography variant="body2" color="text.secondary">
        {explanation}
      </Typography>
      <Typography variant="caption" color="text.secondary">
        {trail.source} • {summarizeAsOf(trail.asOf)}
      </Typography>
    </Stack>
  );
};

interface SummaryState {
  snapshot: SecondOrderSnapshot | null;
  trails: ImpactBundleTrail[];
}

export const SecondOrderSummaryWidget: React.FC<WidgetProps> = ({
  widget,
  onUpdateOutputs,
  onUpdateState,
}) => {
  const config = (widget.config || {}) as Record<string, unknown>;
  const profileId = typeof config.profileId === 'string' ? config.profileId : null;
  const ticker = typeof config.ticker === 'string' ? config.ticker : null;
  const instrumentId = React.useMemo(
    () =>
      resolveInstrumentId({
        instrumentId: typeof config.instrumentId === 'string' ? (config.instrumentId as string) : null,
        profileId,
        ticker,
      }),
    [config.instrumentId, profileId, ticker]
  );

  const [state, setState] = React.useState<SummaryState>({ snapshot: null, trails: [] });
  const [loading, setLoading] = React.useState(false);
  const [error, setError] = React.useState<string | null>(null);
  const [showSignals, setShowSignals] = React.useState(false);

  const loadSnapshot = React.useCallback(
    async (opts?: { silent?: boolean }) => {
      if (!instrumentId) return;
      if (!opts?.silent) setLoading(true);
      setError(null);
      try {
        const res = await getSecondOrderSnapshot(instrumentId, { limit: 36 });
        if (!res) {
          setState({ snapshot: null, trails: [] });
          setError('No second-order impacts yet for this instrument.');
          onUpdateState?.({ loading: false, data: null, error: 'empty' });
        } else {
          setState({ snapshot: res.snapshot, trails: res.trails });
          onUpdateState?.({ loading: false, data: res });
        }
      } catch (err) {
        const message = err instanceof Error ? err.message : 'Failed to load second-order impacts.';
        setError(message);
        onUpdateState?.({ loading: false, error: message });
      } finally {
        if (!opts?.silent) setLoading(false);
      }
    },
    [instrumentId, onUpdateState]
  );

  React.useEffect(() => {
    if (!instrumentId) {
      setState({ snapshot: null, trails: [] });
      setError('Provide a profile or ticker to load second-order impacts.');
      return;
    }
    loadSnapshot();
  }, [instrumentId, loadSnapshot]);

  React.useEffect(() => {
    const selected = showSignals ? 'signalOnly' : 'anchored';
    const current = widget.outputs.find((output) => output.id === 'selectedDomain')?.value;
    if (current !== selected) {
      onUpdateOutputs?.({ selectedDomain: selected });
    }
  }, [showSignals, widget.outputs, onUpdateOutputs]);

  const { anchored, signalOnly } = React.useMemo(() => splitTrails(state.trails), [state.trails]);
  const visible = React.useMemo(() => (showSignals ? signalOnly : anchored), [showSignals, anchored, signalOnly]);
  const topImpacts = React.useMemo(() => {
    const sorted = [...visible].sort((a, b) => {
      const magDiff = Math.abs(b.bundle.magnitude) - Math.abs(a.bundle.magnitude);
      if (Math.abs(magDiff) > 0.0001) return magDiff;
      const confDiff = (b.bundle.confidence || 0) - (a.bundle.confidence || 0);
      if (Math.abs(confDiff) > 0.0001) return confDiff;
      return (b.asOf ?? '').localeCompare(a.asOf ?? '');
    });
    return sorted.slice(0, 3);
  }, [visible]);

  const lastUpdated = summarizeAsOf(state.snapshot?.asOf ?? null);

  return (
    <Stack spacing={1.5} sx={{ height: '100%', px: 1, py: 1.25 }}>
      <Stack direction="row" alignItems="center" spacing={1}>
        <Typography variant="subtitle1" sx={{ flexGrow: 1 }}>
          Second-order Summary
        </Typography>
        <Tooltip title="Refresh impacts">
          <IconButton size="small" onClick={() => loadSnapshot()} disabled={loading || !instrumentId}>
            <RefreshIcon fontSize="small" />
          </IconButton>
        </Tooltip>
      </Stack>

      <Stack direction="row" spacing={1} alignItems="center">
        <Chip
          size="small"
          color={!showSignals ? 'success' : 'default'}
          label={`Anchored ${anchored.length}`}
          onClick={() => setShowSignals(false)}
          clickable
        />
        <Chip
          size="small"
          color={showSignals ? 'warning' : 'default'}
          label={`Signal-only ${signalOnly.length}`}
          onClick={() => signalOnly.length && setShowSignals(true)}
          clickable={signalOnly.length > 0}
          disabled={signalOnly.length === 0}
        />
        <Typography variant="caption" color="text.secondary" sx={{ marginLeft: 'auto' }}>
          Last update {lastUpdated}
        </Typography>
      </Stack>

      {loading ? (
        <Stack alignItems="center" justifyContent="center" sx={{ flexGrow: 1 }}>
          <CircularProgress size={28} />
        </Stack>
      ) : error ? (
        <Alert severity={state.trails.length ? 'warning' : 'info'}>{error}</Alert>
      ) : !instrumentId ? (
        <Alert severity="info">Configure this widget with a profile or ticker to see second-order impacts.</Alert>
      ) : topImpacts.length === 0 ? (
        <Alert severity="info">No {showSignals ? 'signal-only' : 'anchored'} impacts available yet.</Alert>
      ) : (
        <Box sx={{ flexGrow: 1, overflowY: 'auto' }}>
          {topImpacts.map((trail, idx) => (
            <React.Fragment key={`${trail.eventId}-${trail.bundle.affected_metric}-${idx}`}>
              <ImpactRow trail={trail} />
              {idx < topImpacts.length - 1 && <Divider flexItem />}
            </React.Fragment>
          ))}
        </Box>
      )}

      {showSignals && signalOnly.length > 0 && (
        <Alert severity="warning" variant="outlined">
          Signal-only impacts need additional exposure coverage before promotion.
        </Alert>
      )}

      {instrumentId ? (
        <ExposureSnapshot instrumentId={instrumentId} dense sx={{ mt: 0.5 }} />
      ) : null}
    </Stack>
  );
};
